import {
	Button,
	Grid,
	Switch,
	Theme,
	Tooltip,
	makeStyles,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import FormatListBulletedSharpIcon from "@material-ui/icons/FormatListBulletedSharp";
import LockIcon from "@material-ui/icons/Lock";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import React from "react";
import { useEmailTopicsContext } from "../../context/EmailTopicsContext";
import { EmailTopic } from "../../models/EmailTopic";
import { RecipientTypeChips, getRecipientText } from "../RecipientTypeChips";

const useStyles = makeStyles((theme: Theme) => ({
	root: {},
}));

const getAddressesByType = (topic?: EmailTopic) => ({
	TO: topic?.AddressesTO,
	CC: topic?.AddressesCC,
	BCC: topic?.AddressesBCC,
});

type Props = {
	setOpenModalEditTopic: Function;
	setOpenModalDeleteTopic: Function;
	enableEmailTopic: (topic: EmailTopic, enabled: boolean) => void;
	showContextMenu: Function;
};

export const useTableData = ({
	setOpenModalEditTopic,
	setOpenModalDeleteTopic,
	enableEmailTopic,
	showContextMenu,
}: Props) => {
	const { actions } = useEmailTopicsContext();
	const classes = useStyles();

	const columnDefs: ColDef[] = [
		{
			field: "EmailTopicName",
			headerName: "Topic",
			flex: 2,
			minWidth: 160,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "flex-start",
			},
			cellRenderer: ({ data, value }: ICellRendererParams) => (
				<span style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
					{data?.IsSystem ? (
						<Tooltip title="System topic: the application creates and uses it.">
							<LockIcon style={{ fontSize: "0.9rem" }} color="action" />
						</Tooltip>
					) : null}
					{value}
				</span>
			),
		},
		{
			field: "Description",
			headerName: "Description",
			flex: 3,
			minWidth: 160,
			wrapText: true,
			autoHeight: true,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "flex-start",
			},
			valueGetter: ({ data }) => data?.Description?.trim() || "-",
		},
		{
			field: "Addresses",
			headerName: "Addresses",
			flex: 4,
			minWidth: 200,
			wrapText: true,
			autoHeight: true,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "flex-start",
			},
			valueGetter: ({ data }) => getRecipientText(getAddressesByType(data), 8),
			cellRenderer: ({ data }: ICellRendererParams) => (
				<RecipientTypeChips values={getAddressesByType(data)} maxVisible={8} />
			),
		},
		{
			field: "DelayTimeMinutes",
			headerName: "Delay (min)",
			flex: 1,
			minWidth: 90,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			},
			valueGetter: ({ data }) => data?.DelayTimeMinutes ?? "-",
		},
		{
			field: "Enabled",
			headerName: "Enabled",
			flex: 1,
			minWidth: 90,
			sortable: false,
			filter: false,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			},
			cellRenderer: ({ data }: ICellRendererParams) => (
				<Switch
					size="small"
					color="primary"
					checked={!!data?.Enabled}
					onChange={(_event, checked) =>
						enableEmailTopic(data as EmailTopic, checked)
					}
				/>
			),
		},
		{
			headerName: "",
			field: "-",
			sortable: false,
			filter: false,
			flex: 1,
			minWidth: 160,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			},
			cellRenderer: (params: ICellRendererParams) => (
				<Grid
					container
					style={{
						height: "100%",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
					className={classes.root}
				>
					<Grid
						item
						xs={12}
						md={12}
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Button
							size="small"
							style={{ minWidth: 36, padding: 4 }}
							onClick={() => {
								actions.setTopicSelected(params?.data as EmailTopic);
								setOpenModalEditTopic(true);
							}}
						>
							<EditIcon color="primary" fontSize="small" />
						</Button>
						<Button
							size="small"
							style={{ minWidth: 36, padding: 4 }}
							onClick={() => {
								actions.setTopicSelected(params?.data as EmailTopic);
								setOpenModalDeleteTopic(true);
							}}
						>
							<DeleteIcon color="secondary" fontSize="small" />
						</Button>
						<Button
							size="small"
							style={{ minWidth: 36, padding: 4 }}
							onClick={(e) =>
								showContextMenu(e, params.data, "TableEmailTopics")
							}
						>
							<FormatListBulletedSharpIcon color="action" fontSize="small" />
						</Button>
					</Grid>
				</Grid>
			),
		},
	];

	return { columnDefs };
};
